/**
 * Updating a live sender's simulcast ladder without renegotiating.
 *
 * A mid-call quality change calls `applyConstraints`, which reshapes the running
 * capture — and then nothing touched the producer, so the encodings still
 * described the previous frame size. Now that the ladder is sized from
 * `track.getSettings()`, that gap is a real defect: dropping 720p to 320p left
 * three rungs at ÷4/÷2/÷1 describing a 320-wide capture, so we asked for an
 * 80-pixel "thumbnail" and reserved over a megabit for a frame that no longer
 * existed.
 *
 * Why `RTCRtpSender.setParameters` and not the mediasoup-client helper: its
 * `setRtpEncodingParameters` merges ONE params object into EVERY encoding
 * (see any handler's `parameters.encodings.forEach`), so asking for a 150k
 * bottom rung would set all three rungs to 150k. It cannot express a ladder.
 */
import type { LadderEncoding } from './encoding-ladder'

/** The subset of RTCRtpEncodingParameters we set. */
export type SenderEncoding = {
  rid?: string
  active?: boolean
  maxBitrate?: number
  scaleResolutionDownBy?: number
}

/**
 * Map a desired ladder onto the encodings a sender already has.
 *
 * The result ALWAYS keeps the sender's existing length and rid order. Adding or
 * removing encodings on a live sender requires renegotiation; browsers either
 * ignore the attempt or throw, and mediasoup's server-side view would no longer
 * match ours either way. So a ladder that is shorter than the sender simply
 * deactivates the rungs it does not need, and one that is longer is truncated.
 *
 * Rungs are matched low-to-high by position, because both sides are ordered
 * that way — `encoding-ladder` asserts it, and the browser preserves the order
 * it was given at produce time.
 *
 * `maxSpatialLayer` is the server's standing trim — the highest rung any viewer
 * in the room is still asking for. It must be honoured here, because this is
 * the OTHER writer of `active` on the same sender. Without it a camera switch
 * or a quality change silently switched trimmed rungs back on behind the
 * server's back: the ladder reapply said `active: true` for every rung it had a
 * bitrate for, and neither side noticed they disagreed. Two owners of one flag
 * is the bug; the trim wins, and the bitrates and scale factors are still
 * refreshed underneath it so a rung that comes back is sized for the frames
 * being captured now.
 */
export function remapEncodings(
  current: readonly SenderEncoding[],
  desired: readonly LadderEncoding[],
  maxSpatialLayer: number = Infinity,
): SenderEncoding[] {
  return current.map((existing, i) => {
    const want = desired[i]
    if (!want) {
      // Beyond what the new ladder needs. Deactivate rather than drop: the slot
      // has to stay so the array length and rids are unchanged.
      return { ...existing, active: false }
    }
    return {
      ...existing,
      active: i <= maxSpatialLayer,
      maxBitrate: want.maxBitrate,
      scaleResolutionDownBy: want.scaleResolutionDownBy,
    }
  })
}

/** True when nothing would change — so we can skip a pointless setParameters. */
export function encodingsEqual(
  a: readonly SenderEncoding[],
  b: readonly SenderEncoding[],
): boolean {
  if (a.length !== b.length) return false
  return a.every((x, i) => {
    const y = b[i]
    return x.maxBitrate === y.maxBitrate
      && x.scaleResolutionDownBy === y.scaleResolutionDownBy
      && (x.active ?? true) === (y.active ?? true)
  })
}

/**
 * Push a ladder onto a live sender. Resolves to what it did, for logging.
 *
 * Never throws: a browser that refuses a live `scaleResolutionDownBy` change
 * leaves the previous ladder in place, which is exactly today's behaviour and
 * therefore not a regression. Losing the camera over a bitrate tweak would be.
 */
export async function applyLadderToSender(
  sender: { getParameters(): { encodings?: SenderEncoding[] }; setParameters(p: never): Promise<void> } | null | undefined,
  desired: readonly LadderEncoding[],
  maxSpatialLayer: number = Infinity,
): Promise<'applied' | 'unchanged' | 'unavailable' | 'refused'> {
  if (!sender) return 'unavailable'
  try {
    const params = sender.getParameters()
    const current = params.encodings
    if (!current || current.length === 0) return 'unavailable'

    const next = remapEncodings(current, desired, maxSpatialLayer)
    if (encodingsEqual(current, next)) return 'unchanged'

    await sender.setParameters({ ...params, encodings: next } as never)
    return 'applied'
  } catch {
    return 'refused'
  }
}
